import {
  CartDigitalData,
  CartObjectOrderDetails,
  DigitalData,
  RadUiVersion,
} from './digitalData'
import { calculateSubtotalAmount } from '../utils/checkout'
import {
  buildDigitalDataProductDetails,
  replaceDigitalDataChars,
} from './utils'
import { CartItem } from '../types/Product'
import { logger } from '../utils/logger'
import {
  formatPriceInFractionalCurrency,
  formatPriceInWholeCurrency,
} from '../utils/formatting'
import { Currency, Locale } from '../types/locale'

declare global {
  interface Window {
    digitaldata: DigitalData
    _satellite: {
      track: (tag: string) => void
    }
  }
}

export function track(
  tag: string,
  defaultDigitalData: DigitalData,
  currency: Currency,
  locale: Locale,
  options?: {
    cartItems?: CartItem[]
    shippingAmount?: number
    discountAmount?: number
    accordionName?: string
    downloadName?: string
    taxAmount?: string
    orderTotal?: string
  },
): void {
  if (tag === 'ecom-cart-remove' || tag === 'ecom-add') {
    defaultDigitalData.cart = {
      productInfo: buildDigitalDataProductDetails(
        options?.cartItems || [],
        currency,
        locale,
      ),
    }
  } else if (tag === 'ecom-cta') {
    defaultDigitalData.onclick = {
      onclickLinkName: 'fpro:ecom:products:detail:content action',
      onclick:
        'ecom:products:detail:content:accordion expand:' +
        options?.accordionName?.toLowerCase(),
    }
  } else if (tag === 'ecom-download') {
    defaultDigitalData.onclick = {
      onclickLinkName: 'fpro:ecom:products:detail:download',
      onclick: 'ecom:products:detail:download:' + options?.downloadName,
    }
  } else if (tag !== 'ecom-quantity-update' && tag !== 'ecom-referral-exit') {
    defaultDigitalData.cart = buildDigitalDataCartObject(
      tag,
      defaultDigitalData,
      {
        cartItems: options?.cartItems || [],
        shippingAmount: options?.shippingAmount || 0,
        taxAmount: options?.taxAmount || '0',
        orderTotal: options?.orderTotal || '0',
        currency,
        locale,
      },
    )
  }

  window.digitaldata = generateAllDigitalData(
    window.location.hostname,
    window.innerWidth,
    defaultDigitalData,
  )
  if (window._satellite) {
    window._satellite.track(tag)
  } else {
    logger(
      'log',
      `Adobe Analytics satellite was not available yet,` +
        ` so tracking for [${tag}] didn't happen.`,
    )
  }
}

export function generateAllDigitalData(
  hostname: string,
  pageWidth: number,
  digitalData: DigitalData,
): DigitalData {
  const commonDigitalData: Partial<DigitalData> = {
    page: {
      client: digitalData.page.client ?? 'fpro-us',
      radUIVersion: getRadUiVersion(pageWidth),
      site: digitalData.page.site ?? hostname,
      siteSection: digitalData.page.siteSection ?? 'ecom',
      userLanguage: digitalData.page.userLanguage ?? 'eng',
    },
  }
  digitalData.page = { ...digitalData.page, ...commonDigitalData.page }
  logger('log', 'digitalData:', digitalData)
  return digitalData
}

function getRadUiVersion(pageWidth: number): RadUiVersion {
  if (pageWidth < 768) {
    return 'ui:rad:mobile'
  }
  if (pageWidth < 992) {
    return 'ui:rad:tablet'
  }
  return 'ui:rad:pc'
}

export function buildDigitalDataCartObject(
  directCallName: string,
  digitalData: DigitalData,
  orderDetails: CartObjectOrderDetails,
) {
  const { cartItems, shippingAmount, taxAmount, orderTotal, currency, locale } =
    orderDetails
  let cart: CartDigitalData
  if (directCallName === 'ecom-cart-empty') {
    cart = {
      subTotal: 'ecom:cart empty page',
      cartTotal: 'ecom:cart empty page',
    }
  } else {
    const subtotalAmount = calculateSubtotalAmount(cartItems, currency)

    cart = {
      orderNumber:
        digitalData?.cart?.orderNumber !== ''
          ? digitalData?.cart?.orderNumber
          : 'no-order-number',
      subTotalNoDiscount: replaceDigitalDataChars(
        formatPriceInWholeCurrency(subtotalAmount, currency, locale),
      ),
      subTotal: replaceDigitalDataChars(
        formatPriceInWholeCurrency(subtotalAmount, currency, locale),
      ),
      discountAmount: replaceDigitalDataChars(
        formatPriceInFractionalCurrency(0, currency, locale),
      ),
      shippingCost: replaceDigitalDataChars(shippingAmount.toString()),
      taxes: replaceDigitalDataChars(taxAmount.toString()),
      cartTotal: replaceDigitalDataChars(orderTotal),
      discountCode: 'no-discount-code',
      deliveryMethod: 'no-delivery-method',
      productInfo: buildDigitalDataProductDetails(cartItems, currency, locale),
    }
  }
  return cart
}
